import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Dropdown, Empty } from 'antd';
import {
  ApiOutlined,
  CloudServerOutlined,
  CodeSandboxOutlined,
  FieldTimeOutlined,
  GoldOutlined,
  HistoryOutlined,
  PlusOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { CollectionSidebar } from '@presentation/components/workbench/CollectionSidebar';
import { RequestTabsBar } from '@presentation/components/workbench/RequestTabsBar';
import { RequestBuilderPanel } from '@presentation/components/workbench/RequestBuilderPanel';
import { EnvironmentSwitcher } from '@presentation/components/workbench/EnvironmentSwitcher';
import { HistoryPanel } from '@presentation/components/workbench/HistoryPanel';
import { CookieJarDrawer } from '@presentation/components/workbench/CookieJarDrawer';
import { WebSocketDrawer } from '@presentation/components/workbench/WebSocketDrawer';
import { SseDrawer } from '@presentation/components/workbench/SseDrawer';
import { MockServerDrawer } from '@presentation/components/workbench/MockServerDrawer';
import { ActivityLogDrawer } from '@presentation/components/workbench/ActivityLogDrawer';
import { ResizableSidebar } from '@presentation/components/workbench/ResizableSidebar';
import { ResizableSplit } from '@presentation/components/workbench/ResizableSplit';
import { ConsolePanel } from '@presentation/components/workbench/ConsolePanel';
import { CommandPalette } from '@presentation/components/workbench/CommandPalette';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';
import { useConsoleStore } from '@presentation/stores/consoleStore';
import { useCollections } from '@presentation/hooks/useCollections';
import { STORAGE_KEYS } from '@shared/constants';

export const WorkbenchPage = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cookiesOpen, setCookiesOpen] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const [sseOpen, setSseOpen] = useState(false);
  const [mockServersOpen, setMockServersOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const tabs = useWorkbenchStore((state) => state.tabs);
  const activeKey = useWorkbenchStore((state) => state.activeKey);
  const openNewRequestTab = useWorkbenchStore((state) => state.openNewRequestTab);
  const reopenLastClosedTab = useWorkbenchStore((state) => state.reopenLastClosedTab);
  const { data: collections } = useCollections(workspaceId);
  const consoleEntryCount = useConsoleStore((state) => state.entries.length);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;

      if (event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((value) => !value);
      } else if (event.shiftKey && event.key.toLowerCase() === 't') {
        event.preventDefault();
        reopenLastClosedTab();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reopenLastClosedTab]);

  if (!workspaceId) return null;

  const activeTab = tabs.find((tab) => tab.key === activeKey);
  const firstCollectionId = collections?.[0]?.id;

  const mainContent = (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-black/10 px-3 py-1.5 dark:border-white/10">
        <div className="shrink-0">
          <EnvironmentSwitcher workspaceId={workspaceId} />
        </div>
        <div className="flex min-w-0 shrink items-center gap-2 overflow-x-auto">
          <Button size="small" icon={<SearchOutlined />} onClick={() => setPaletteOpen(true)}>
            Search
            <span className="ml-1 rounded border border-black/10 px-1 text-[10px] text-gray-400 dark:border-white/10">
              ⌘K
            </span>
          </Button>
          <Badge dot={!consoleOpen && consoleEntryCount > 0} offset={[-4, 2]}>
            <Button
              size="small"
              icon={<CodeSandboxOutlined />}
              onClick={() => setConsoleOpen((value) => !value)}
            >
              Console
            </Button>
          </Badge>
          <Button size="small" icon={<GoldOutlined />} onClick={() => setCookiesOpen(true)}>
            Cookies
          </Button>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            History
          </Button>
          <Dropdown
            menu={{
              items: [
                { key: 'websocket', icon: <ThunderboltOutlined />, label: 'WebSocket Client' },
                { key: 'sse', icon: <ApiOutlined />, label: 'Server-Sent Events Client' },
                { key: 'mock-servers', icon: <CloudServerOutlined />, label: 'Mock Servers' },
                { key: 'activity-log', icon: <FieldTimeOutlined />, label: 'Activity Log' },
              ],
              onClick: ({ key }) => {
                if (key === 'websocket') setWsOpen(true);
                else if (key === 'sse') setSseOpen(true);
                else if (key === 'mock-servers') setMockServersOpen(true);
                else if (key === 'activity-log') setActivityLogOpen(true);
              },
            }}
          >
            <Button size="small" icon={<ThunderboltOutlined />}>
              Tools
            </Button>
          </Dropdown>
        </div>
      </div>

      <RequestTabsBar
        onNewTab={() => {
          if (firstCollectionId) {
            openNewRequestTab(workspaceId, firstCollectionId, null);
          }
        }}
      />

      <div className="min-h-0 flex-1 overflow-hidden">
        {activeTab ? (
          <RequestBuilderPanel key={activeTab.key} tab={activeTab} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Empty description="No request open">
              {firstCollectionId && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openNewRequestTab(workspaceId, firstCollectionId, null)}
                >
                  New request
                </Button>
              )}
            </Empty>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-56px)] overflow-hidden">
      <ResizableSidebar
        storageKey={STORAGE_KEYS.apiSidebarWidth}
        defaultWidthPx={280}
        collapsedWidthPx={220}
        minPx={200}
        maxPx={560}
      >
        <CollectionSidebar workspaceId={workspaceId} />
      </ResizableSidebar>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {/*
          Always rendered through the same ResizableSplit, toggling only
          `bottomHidden` — conditionally swapping between rendering
          `mainContent` bare vs. wrapped in ResizableSplit put it at a
          different position/shape in the tree on every consoleOpen flip,
          which made React remount everything inside (losing the active
          Params/Headers/.../Scripts tab selection, among other in-progress
          UI state) each time the Console panel was opened or closed.
        */}
        <ResizableSplit
          storageKey={STORAGE_KEYS.apiConsoleHeight}
          defaultTopPercent={75}
          expandedTopPercent={40}
          minPx={120}
          maxPercent={85}
          bottomHidden={!consoleOpen}
          top={mainContent}
          bottom={<ConsolePanel onClose={() => setConsoleOpen(false)} />}
        />
      </div>

      <HistoryPanel
        workspaceId={workspaceId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <CookieJarDrawer
        workspaceId={workspaceId}
        open={cookiesOpen}
        onClose={() => setCookiesOpen(false)}
      />
      <WebSocketDrawer open={wsOpen} onClose={() => setWsOpen(false)} />
      <SseDrawer open={sseOpen} onClose={() => setSseOpen(false)} />
      <MockServerDrawer
        workspaceId={workspaceId}
        open={mockServersOpen}
        onClose={() => setMockServersOpen(false)}
      />
      <ActivityLogDrawer
        workspaceId={workspaceId}
        open={activityLogOpen}
        onClose={() => setActivityLogOpen(false)}
      />
      <CommandPalette
        workspaceId={workspaceId}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenCookies={() => setCookiesOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenConsole={() => setConsoleOpen(true)}
      />
    </div>
  );
};
